import React, { useEffect, useState } from "react";
import { Layers3, Plus, Send, Trash2, Type } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { useStudioAuth } from "../StudioAuthContext";
import { EmptyState, FieldLabel, OPS_INPUT, Panel, StatusChip } from "../ui";

const sourceTypes = ["weatherfront", "camera", "forecast", "live"];
const sourceLabels = { weatherfront: "Storm Desk Radar", camera: "Camera", forecast: "Forecast", live: "Live" };
const layouts = ["full", "split", "warning"];
const builtIn = new Set(["radar_full", "chaser_cam", "forecast", "breaking_news", "analysis", "live_source"]);
const chipClass = (active) =>
  `h-8 border px-2 text-[10px] font-bold uppercase tracking-wider transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400 ${active ? "border-sky-500 bg-sky-500/15 text-white" : "border-white/10 text-slate-500 hover:border-white/20 hover:text-slate-200"}`;

export const ScenesSection = () => {
  const { request } = useStudioAuth();
  const [scenes, setScenes] = useState([]);
  const [broadcast, setBroadcast] = useState(null);
  const [form, setForm] = useState({ name: "", source_type: "weatherfront", layout: "full" });
  const [lowerThird, setLowerThird] = useState({ title: "", subtitle: "" });

  const load = async () => {
    const [sceneResponse, broadcastResponse] = await Promise.all([request("/operations/scenes"), request("/broadcast")]);
    if (sceneResponse.ok) setScenes(await sceneResponse.json());
    if (broadcastResponse.ok) {
      const data = await broadcastResponse.json();
      setBroadcast(data);
      setLowerThird({ title: data.lower_third_title || "", subtitle: data.lower_third_subtitle || "" });
    }
  };

  useEffect(() => { load(); }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const createScene = async (event) => {
    event.preventDefault();
    const response = await request("/operations/scenes", { method: "POST", body: JSON.stringify(form) });
    if (!response.ok) return toast.error("Scene could not be saved");
    setForm({ name: "", source_type: "weatherfront", layout: "full" });
    await load();
    toast.success("Scene saved");
  };

  const deleteScene = async (sceneId) => {
    const response = await request(`/operations/scenes/${sceneId}`, { method: "DELETE" });
    if (!response.ok) return toast.error("Scene could not be deleted");
    setScenes((items) => items.filter((item) => item.id !== sceneId));
  };

  const sendToPreview = async (sceneId) => {
    const response = await request("/broadcast", { method: "POST", body: JSON.stringify({ preview_scene: sceneId }) });
    if (!response.ok) return toast.error("Preview could not be updated");
    setBroadcast(await response.json());
    toast.success("Scene loaded into Preview");
  };

  const applyLowerThird = async () => {
    const response = await request("/broadcast", { method: "POST", body: JSON.stringify({ lower_third_title: lowerThird.title, lower_third_subtitle: lowerThird.subtitle }) });
    if (response.ok) toast.success("Lower third staged for Preview");
    else toast.error("Lower third could not be staged");
  };

  return (
    <div className="grid gap-3 xl:grid-cols-[1.35fr_.65fr]">
      <Panel title="Scene Library" icon={Layers3} note={`${scenes.length} scenes`} bodyClass="p-0">
        {scenes.length === 0 ? (
          <EmptyState icon={Layers3} title="No scenes yet" message="Create a scene on the right to make it selectable on the scene bus." testId="scenes-empty-state" />
        ) : (
          <div className="grid gap-px bg-[rgba(120,170,255,0.12)] sm:grid-cols-2 2xl:grid-cols-3">
            {scenes.map((scene) => {
              const staged = broadcast?.preview_scene === scene.id;
              const onAir = broadcast?.scene === scene.id;
              return (
                <div key={scene.id} className="flex flex-col justify-between bg-[#0a0f1d] p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="display truncate text-sm font-bold uppercase text-white">{scene.name}</div>
                      <div className="mono mt-1 text-[9px] uppercase tracking-widest text-slate-600">{scene.source_type} · {scene.layout}</div>
                    </div>
                    {!builtIn.has(scene.id) && (
                      <button data-testid={`delete-scene-${scene.id}`} onClick={() => deleteScene(scene.id)} title="Delete scene" className="grid h-7 w-7 shrink-0 place-items-center text-slate-600 transition-colors hover:bg-red-500/10 hover:text-red-400">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    {onAir ? <StatusChip state="live">On air</StatusChip> : staged ? <StatusChip state="preview">Staged</StatusChip> : <span className="mono text-[9px] uppercase tracking-widest text-slate-700">Idle</span>}
                  </div>
                  <Button data-testid={`send-scene-${scene.id}-to-preview`} onClick={() => sendToPreview(scene.id)} className="mt-3 h-8 w-full rounded-none bg-sky-600 text-[10px] font-bold uppercase tracking-widest hover:bg-sky-500">
                    <Send className="h-3.5 w-3.5" /> Send to Preview
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <div className="space-y-3">
        <Panel title="New Scene" icon={Plus} bodyClass="p-4">
          <form onSubmit={createScene} className="space-y-4">
            <div className="space-y-1">
              <FieldLabel htmlFor="scene-name">Name</FieldLabel>
              <Input id="scene-name" data-testid="scene-name-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required placeholder="Storm coverage" className={OPS_INPUT} />
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Source</FieldLabel>
              <div className="grid grid-cols-2 gap-1">
                {sourceTypes.map((type) => (
                  <button type="button" key={type} data-testid={`scene-source-${type}`} onClick={() => setForm({ ...form, source_type: type })} className={chipClass(form.source_type === type)}>{sourceLabels[type]}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Layout</FieldLabel>
              <div className="grid grid-cols-3 gap-1">
                {layouts.map((layout) => (
                  <button type="button" key={layout} data-testid={`scene-layout-${layout}`} onClick={() => setForm({ ...form, layout })} className={chipClass(form.layout === layout)}>{layout}</button>
                ))}
              </div>
            </div>
            <Button data-testid="save-scene-button" type="submit" className="h-9 w-full rounded-none bg-[#1a5cff] text-[11px] font-bold uppercase tracking-widest hover:bg-[#2a6cff]">
              <Plus className="h-4 w-4" /> Save Scene
            </Button>
          </form>
        </Panel>

        <Panel title="Lower Third" icon={Type} note="Stages onto Preview" bodyClass="space-y-2 p-4">
          <Input data-testid="lower-third-title-input" value={lowerThird.title} onChange={(event) => setLowerThird({ ...lowerThird, title: event.target.value })} placeholder="Headline" className={OPS_INPUT} />
          <Input data-testid="lower-third-subtitle-input" value={lowerThird.subtitle} onChange={(event) => setLowerThird({ ...lowerThird, subtitle: event.target.value })} placeholder="Location or details" className={OPS_INPUT} />
          <Button data-testid="prepare-lower-third-button" onClick={applyLowerThird} className="h-9 w-full rounded-none border border-white/10 bg-white/[0.04] text-[11px] font-bold uppercase tracking-widest text-white hover:bg-white/10">
            Stage Graphic
          </Button>
        </Panel>
      </div>
    </div>
  );
};

export default ScenesSection;
